<template>
  <footer
    class="m-footer"
    :class="[$region]"
  >
    <div class="top tw-flex tw-justify-between">
      <div class="left tw-flex-1">
        <!-- logo -->
        <div class="tw-mb-[40px]">
          <img
            class="tw-mb-[20px]"
            :src="themeImages.mSportLogo"
            loading="lazy"
            alt="MSport"
            width="115px"
            height="18px"
          >

          <img
            v-if="config.gamingLicenseLogo"
            :class="{
              'tw-w-[55px]': $region === 'gh',
              'tw-w-[80px]': $region === 'ug'
            }"
            :src="config.gamingLicenseLogo"
            loading="lazy"
            alt=""
          >

          <!-- license -->
          <p data-nosnippet>
            {{ $t('LICENSE') }}
          </p>
        </div>

        <!-- age limit -->
        <div class="tw-mb-[30px]">
          <div class="limit-age">
            {{ $t('LEGAL_AGE') }}<span class="icon-plus">+</span>
          </div>

          <p>
            {{ $t('LEGAL_AGE_WARNING') }}
          </p>
        </div>

        <Payments class="tw-mb-[30px]" />
        <PaybillNumber class="paybill tw-mb-[30px]" />

        <div>
          <h3 class="m-title tw-mb-[10px]">
            Sponsorship
          </h3>
          <img
            class="tw-mb-[10px]"
            :src="themeImages.chelseaIcon"
            loading="lazy"
            height="30px"
          >
          <img
            :src="themeImages.bvbIcon"
            loading="lazy"
            height="30px"
          >
        </div>
      </div>

      <div class="right tw-flex-1">
        <!-- General -->
        <div class="m-navs tw-mb-[30px]">
          <h3 class="m-title tw-mb-[20px]">
            General
          </h3>

          <div
            v-for="(item, index) in generalNavs"
            :key="index"
            class="nav-item"
          >
            <a
              :href="item.href"
              class="x-link-active"
            >{{ item.label }}</a>
          </div>
        </div>

        <!-- Customer Service -->
        <div class="m-services tw-mb-[40px]">
          <h3 class="m-title tw-mb-[20px]">
            Customer Service
          </h3>

          <div
            class="m-contact"
            :class="{
              'no-tel': !telContact
            }"
          >
            <a
              :href="supportContactUs"
              class="m-contact-item x-link-active"
            >
              <img
                class="icon-service"
                src="~public/images/icon/customer-service.svg"
                loading="lazy"
                alt=""
              >
              <div class="content">
                <span class="m-label">24/7 Online Help</span>
              </div>
            </a>
            <template v-if="telContact">
              <div class="m-contact-item">
                <a
                  :href="`${telContact.length > 1 ? 'javascript:;': 'tel:' + telContact[0].value}`"
                  class="m-contact-item x-link-active"
                >
                  <img
                    class="icon-service"
                    src="~public/images/icon/callus.svg"
                    loading="lazy"
                    alt=""
                  >
                  <div class="content">
                    <span class="m-label">Call Us</span>
                    <span class="m-value">
                      <template v-if="telContact.length > 1">
                        <div
                          v-for="tel in telContact"
                          :key="tel.label"
                          class="item-tel"
                          @click="togglePopTel(true)"
                        >{{ tel.label }}</div>
                      </template>
                      <div
                        v-else
                        class="item-tel"
                      >{{ telContact[0].label }}</div>
                    </span>
                  </div>
                </a>
              </div>
            </template>
          </div>
        </div>

        <!-- change region -->
        <div class="m-platforms">
          <DialogRegion
            v-if="hasPlatforms"
            :region-redirect-url="regionRedirectUrl"
            :autocheck="autocheck"
            source="Footer"
          >
            <div class="m-region">
              <img
                :src="$t('URL_FLAG')"
                loading="lazy"
                alt=""
              >
              <span class="tw-mx-1">{{ $t('COUNTRY_NAME') }}</span>
              <i class="m-trangle down" />
            </div>
          </DialogRegion>

          <Socials
            :facebook-home="$t('FACEBOOK_HOME')"
            :twitter-home="$t('TWITTER_HOME')"
            :youtube-home="youtubeHome"
          >
            <a
              v-if="hasWebsite"
              href="/"
            >{{ VUE_APP_HOSTNAME }} >></a>
          </Socials>
        </div>

        <div>
          <a
            :href="appLinks.appIosAppleStore"
            class="x-link-active tw-block tw-w-[120px] tw-mb-[16px]"
            aria-label="Download on the App Store"
            target="_blank"
          >
            <img
              :src="themeImages.appStoreDownloadImage"
              loading="lazy"
              alt="Download on the App Store"
              width="120"
              height="40"
            >
          </a>

          <a
            :href="appLinks.appAndroidApk"
            class="x-link-active tw-block tw-w-[120px]"
            download="MSport"
            aria-label="Download for android"
            target="_blank"
          >
            <img
              :src="themeImages.androidDownloadImage"
              loading="lazy"
              alt="Download for android"
              width="120"
              height="40"
            >
          </a>
        </div>

        <p
          v-if="hasHarmfulTip"
          style="margin-bottom: 20px;"
        >
          Betting is addictive and can be psychologically harmful.
        </p>
      </div>
    </div>

    <!-- copyright -->
    <p class="tw-text-center tw-mt-[80px]">
      &copy; {{ copyYear }} MSport. All rights reserved.
    </p>

    <PopTel
      v-if="visibleTel"
      :tel-contact="telContact"
      @close="togglePopTel(false)"
    />
  </footer>
</template>

<script>
import { storeToRefs } from 'pinia'
import { REGION, appLinks, prefixLink, URL_TERMS_CONDITIONS } from '@ms/config/app'
import { useGlobalConfigStore } from '@ms/stores/globalConfig'
import DialogRegion from '@ms/components/Region/DialogRegion.vue'
import PaybillNumber from '@ms/components/PaybillNumber.vue'
import PopTel from 'wap/components/PopTel/PopTel.vue'
import usePopTel from 'wap/components/PopTel/usePopTel.js'
import Socials from './pagelet/Socials.vue'
import Payments from './pagelet/Payments.vue'
import { useThemeImages } from 'composables/useThemeImages.js'

const config = require(`./${REGION}/config.js`).default

export default {
  name: 'LayoutFooter',
  components: {
    DialogRegion,
    PaybillNumber,
    PopTel,
    Socials,
    Payments
  },
  props: {
    hasWebsite: {
      type: Boolean,
      default: false
    },
    hasPlatforms: {
      type: Boolean,
      default: true
    },
    hasHarmfulTip: {
      type: Boolean,
      default: REGION === 'ug' // ug 默认显示 harmful 提示
    },
    regionRedirectUrl: {
      type: String,
      default: ''
    },
    // 自动检测国家版本
    autocheck: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { telContact } = storeToRefs(useGlobalConfigStore())
    const { visibleTel, togglePopTel } = usePopTel()

    const themeImages = useThemeImages({
      dark: {
        default: {
          mSportLogo: require('public/images/logo/logo.png'),
          chelseaIcon: require('public/images/chelsea.png'),
          bvbIcon: require('public/images/bvb@2x.png'),
          androidDownloadImage: require('public/images/download-android@2x.png'),
          appStoreDownloadImage: require('public/images/download-appstore@2x.png')
        }
      },
      light: {
        default: {
          mSportLogo: require('public/images/logo/logo-dark.png'),
          chelseaIcon: require('public/images/chelsea[light-default].png'),
          bvbIcon: require('public/images/bvb@2x[light-default].png'),
          androidDownloadImage: require('public/images/download-android@2x[light-default].png'),
          appStoreDownloadImage: require('public/images/download-appstore@2x[light-default].png')
        }
      }
    })

    return {
      appLinks,
      VUE_APP_HOSTNAME: process.env.VUE_APP_HOSTNAME,
      telContact,
      visibleTel,
      togglePopTel,
      youtubeHome: process.env.VUE_APP_YOUTUBE,
      themeImages
    }
  },
  data() {
    return {
      config,
      supportContactUs: prefixLink('/support/about/contact-us?chat=1'),
      homeUrl: prefixLink(),
      copyYear: new Date().getFullYear(),
      generalNavs: [
        { label: 'T&C', href: URL_TERMS_CONDITIONS },
        { label: 'How to Play', href: prefixLink('/support/how-to-play') },
        { label: 'About Us', href: prefixLink('/support/about') },
        { label: 'Contact Us', href: prefixLink('/support/about/contact-us') }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@use "wap/common/style/mixin.scss" as mixin;

.m-footer {
  padding: 30px 20px;
  margin-top: 40px;
  background: #161616;
  color: #666;
  font-size: 12px;
  line-height: 16px;

  a {
    color: #666;
  }

  .top {
    .left {
      width: calc(181/335*100%);
      box-sizing: border-box;
      padding-right: 30px;
    }

    .right {
      width: calc(154/335*100%);
    }

    ::v-deep .paybill .title,
    .m-title {
      color: #fff;
      font-size: 16px;
      line-height: 20px;
      white-space: nowrap;
    }
  }

  .m-platforms {
    .m-region {
      display: flex;
      align-items: center;
      color: #fff;
      font-size: 13px;

      > img {
        width: 30px;
        border: 1px solid #fff;
      }

      > .m-trangle {
        display: inline-block;
        margin-top: 2px;
        vertical-align: middle;

        @include mixin.m-icon-trangle(4px, #fff);
      }
    }
  }


  .m-navs {
    .nav-item {
      margin-bottom: 12px;
      line-height: 18px;
    }
  }

  .m-payments {
    ::v-deep .m-title {
      margin-bottom: 15px;
      color: #fff;
      font-size: 16px;
      line-height: 20px;
      white-space: nowrap;
    }
  }

  .m-services {
    .m-contact {
      display: flex;
      flex-direction: column;

      .m-contact-item {
        display: flex;
        align-items: center;
        margin-top: 18px;
        column-gap: 8px;
        line-height: 16px;

        .icon-service {
          width: 18px;
          align-self: flex-start;
        }

        .content {
          display: flex;
          flex-direction: column;
        }

        &:first-child {
          margin-top: 0;
        }
      }
    }
  }

  ::v-deep .paybill-number {
    color: #666;
    font-size: 18px;
    line-height: 22px;
  }

  .limit-age {
    width: 20px;
    height: 20px;
    border: 1px solid #a8261d;
    border-radius: 50%;
    margin-bottom: 10px;
    color: #a8261d;
    font-size: 10px;
    line-height: 20px;
    text-align: center;
    white-space: nowrap;

    .icon-plus {
      display: inline-block;
      margin-top: 4px;
      line-height: 1;
      vertical-align: top;
    }
  }
}

[data-mode="light"] .m-footer {
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0,0,0,7%);
  color: #999;

  a {
    color: #999;
  }

  .top {
    .paybill .title,
    .m-title {
      color: #000;
    }
  }

  .m-payments {
    ::v-deep .m-title {
      color: #000;
    }
  }

  .m-platforms {
    .m-region {
      color: #000;

      > .m-trangle {
        @include mixin.m-icon-trangle(4px, #999);
      }
    }
  }
}

</style>
